'use client';

import React, { useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { PaginatedCards } from '@/src/types/card';
import LogoLoader from '../LogoLoader/LogoLoader';
import PageHeader from '../ui/PageHeader';
import { ProductCardGrid } from '../ProductCardGrid/ProductCardGrid';
import { useProductListing } from './useProductListing';
import { CategorySlider } from './CategorySlider';
import { TuneIcon, CloseIcon, FirstPageIcon, ChevronLeftIcon, ChevronRightIcon, LastPageIcon } from '../icons';
import styles from './ProductListing.module.css';

interface ProductListingProps {
    initialCategories: string[];
    initialCountries: string[];
    categoryFromUrl?: string;
    countryFromUrl?: string;
    initialProducts: PaginatedCards;
    h1?: string;
    h2?: string;
    underH2?: string;
}

const FilterSidebarContent = dynamic(() => import('./FilterSidebar').then(mod => ({ default: mod.FilterSidebarContent })), { ssr: false });

export const ProductListing: React.FC<ProductListingProps> = ({ initialProducts, initialCategories, initialCountries, categoryFromUrl, countryFromUrl, h1, h2, underH2 }) => {
    const t = useTranslations('giftcards');
    const {
        loading,
        cards,
        countries,
        categories,
        totalCards,
        page,
        selectedCategory,
        selectedCountry,
        showDiscount,
        showInStock,
        mobileDrawerOpen,
        categoriesExpanded,
        countriesExpanded,
        categorySearchFilter,
        countrySearchFilter,
        totalPages,
        breadcrumbs,
        filteredCategories,
        filteredCountries,
        cardsPerPage,
        isMobile,
        handlePageChange,
        handleMobileDrawerToggle,
        handleCategoryClick,
        handleCountryClick,
        handleDiscountToggle,
        handleInStockToggle,
        handleClearAllFilters,
        getActiveFilters,
        handleItemsPerPageChange,
        ITEMS_PER_PAGE_OPTIONS,
        setCategoriesExpanded,
        setCountriesExpanded,
        setCategorySearchFilter,
        setCountrySearchFilter
    } = useProductListing({ categoryFromUrl, initialProducts, countryFromUrl, initialCategories, initialCountries });

    const paginationPages = useMemo(() => {
        const pages: (number | 'ellipsis')[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('ellipsis');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
            if (page < totalPages - 2) pages.push('ellipsis');
            pages.push(totalPages);
        }
        return pages;
    }, [page, totalPages]);

    // Pagination controls are real <a href> links so crawlers can discover
    // pages >= 2 (Google follows links, not button clicks). Plain
    // left-clicks keep the existing SPA behavior via preventDefault; modifier/
    // middle clicks fall through to the browser for open-in-new-tab.
    const pathname = usePathname();
    const currentSearchParams = useSearchParams();

    const buildPageHref = useCallback((p: number) => {
        const params = new URLSearchParams(currentSearchParams?.toString());
        if (p <= 1) {
            params.delete('page');
        } else {
            params.set('page', String(p));
        }
        const queryString = params.toString();
        return queryString ? `${pathname}?${queryString}` : pathname;
    }, [pathname, currentSearchParams]);

    const handlePageLinkClick = useCallback((p: number) => (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        handlePageChange(null as unknown as React.ChangeEvent<unknown>, p);
    }, [handlePageChange]);

    const filterSidebarProps = {
        activeFilters: getActiveFilters(),
        onClearAllFilters: handleClearAllFilters,
        categories,
        selectedCategory,
        categoriesExpanded,
        categorySearchFilter,
        filteredCategories,
        onCategoryClick: handleCategoryClick,
        onToggleCategoriesExpanded: () => setCategoriesExpanded(!categoriesExpanded),
        onCategorySearchChange: setCategorySearchFilter,
        countries,
        selectedCountry,
        countriesExpanded,
        countrySearchFilter,
        filteredCountries,
        onCountryClick: handleCountryClick,
        onToggleCountriesExpanded: () => setCountriesExpanded(!countriesExpanded),
        onCountrySearchChange: setCountrySearchFilter,
        showDiscount,
        onDiscountToggle: handleDiscountToggle,
        showInStock,
        onInStockToggle: handleInStockToggle,
    };

    return (
        <>
            <PageHeader breadcrumbs={breadcrumbs} h1={h1} h2={h2} underH2={underH2} />
            
            <div className={styles.mobileCategoryArea}>
                <CategorySlider />
            </div>

            <div className={styles.pageContainer}>
                <div className={styles.gridContainer}>
                    <div className={styles.desktopOnly}>
                        <div className={styles.filterSidebar}>
                            <FilterSidebarContent {...filterSidebarProps} />
                        </div>
                    </div>

                    {/* Mobile Drawer */}
                    <div className={`${styles.mobileDrawerOverlay} ${mobileDrawerOpen ? styles.open : ''}`} onClick={handleMobileDrawerToggle} />
                    <div className={`${styles.mobileDrawerPanel} ${mobileDrawerOpen ? styles.open : ''}`}>
                        <div className={styles.mobileDrawerHeader}>
                            <span className={styles.mobileDrawerTitle}>Filters</span>
                            <button className={styles.mobileDrawerClose} onClick={handleMobileDrawerToggle}>
                                <CloseIcon />
                            </button>
                        </div>
                        <div className={styles.mobileDrawerBody}>
                            <FilterSidebarContent {...filterSidebarProps} />
                        </div>
                    </div>

                    <button className={styles.mobileFilterButton} onClick={handleMobileDrawerToggle}>
                        <TuneIcon />
                    </button>

                    <div>
                        <div className={styles.mainSection}>
                            {loading ? (
                                <div className={styles.loaderContainer}>
                                    <LogoLoader size={50} color="var(--color-text)" />
                                </div>
                            ) : (
                                <>
                                    <div className={styles.resultsInfo}>
                                        <p className={styles.resultsCount}>
                                            {totalCards} {t('results.cardsFound')}
                                        </p>
                                        
                                        <div className={styles.itemsPerPageContainer}>
                                            <span className={styles.itemsPerPageLabel}>
                                                {t('results.itemsPerPage')}
                                            </span>
                                            <select
                                                className={styles.itemsPerPageSelect}
                                                value={cardsPerPage}
                                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                            >
                                                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                                                    <option key={option} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {cards.length > 0 ? (
                                        <ProductCardGrid 
                                            cards={cards}
                                            spacing={1}
                                            eagerLoadCount={8}
                                            itemsPerRow={
                                                isMobile 
                                                    ? { xs: 12, sm: 6 }
                                                    : { xs: 12, sm: 6, lg: 3 }
                                            }
                                        />
                                    ) : (
                                        <div className={styles.noResults}>
                                            <h3>{t('results.noResults.title')}</h3>
                                            <p>{t('results.noResults.description')}</p>
                                        </div>
                                    )}
                                    {totalPages > 1 && (
                                        <div className={styles.paginationContainer}>
                                            <nav className={styles.pagination} aria-label="Product pages navigation">
                                                {page === 1 ? (
                                                    <span className={`${styles.paginationButton} ${styles.paginationButtonDisabled}`} aria-disabled="true">
                                                        <FirstPageIcon fontSize="small" />
                                                    </span>
                                                ) : (
                                                    <a
                                                        className={styles.paginationButton}
                                                        href={buildPageHref(1)}
                                                        aria-label="First page"
                                                        onClick={handlePageLinkClick(1)}
                                                    >
                                                        <FirstPageIcon fontSize="small" />
                                                    </a>
                                                )}
                                                {page === 1 ? (
                                                    <span className={`${styles.paginationButton} ${styles.paginationButtonDisabled}`} aria-disabled="true">
                                                        <ChevronLeftIcon fontSize="small" />
                                                    </span>
                                                ) : (
                                                    <a
                                                        className={styles.paginationButton}
                                                        href={buildPageHref(page - 1)}
                                                        rel="prev"
                                                        aria-label="Previous page"
                                                        onClick={handlePageLinkClick(page - 1)}
                                                    >
                                                        <ChevronLeftIcon fontSize="small" />
                                                    </a>
                                                )}
                                                {paginationPages.map((p, i) =>
                                                    p === 'ellipsis' ? (
                                                        <span key={`ellipsis-${i}`} className={styles.paginationEllipsis}>…</span>
                                                    ) : (
                                                        <a
                                                            key={p}
                                                            className={`${styles.paginationButton} ${page === p ? styles.paginationButtonActive : ''}`}
                                                            href={buildPageHref(p)}
                                                            aria-current={page === p ? 'page' : undefined}
                                                            onClick={handlePageLinkClick(p)}
                                                        >
                                                            {p}
                                                        </a>
                                                    )
                                                )}
                                                {page === totalPages ? (
                                                    <span className={`${styles.paginationButton} ${styles.paginationButtonDisabled}`} aria-disabled="true">
                                                        <ChevronRightIcon fontSize="small" />
                                                    </span>
                                                ) : (
                                                    <a
                                                        className={styles.paginationButton}
                                                        href={buildPageHref(page + 1)}
                                                        rel="next"
                                                        aria-label="Next page"
                                                        onClick={handlePageLinkClick(page + 1)}
                                                    >
                                                        <ChevronRightIcon fontSize="small" />
                                                    </a>
                                                )}
                                                {page === totalPages ? (
                                                    <span className={`${styles.paginationButton} ${styles.paginationButtonDisabled}`} aria-disabled="true">
                                                        <LastPageIcon fontSize="small" />
                                                    </span>
                                                ) : (
                                                    <a
                                                        className={styles.paginationButton}
                                                        href={buildPageHref(totalPages)}
                                                        aria-label="Last page"
                                                        onClick={handlePageLinkClick(totalPages)}
                                                    >
                                                        <LastPageIcon fontSize="small" />
                                                    </a>
                                                )}
                                            </nav>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
